import type { SDKState } from "@/state/sdk-state";
import { SimpleObservable, SimpleObservableSubject } from "@/utils/observable";
import * as z from "zod/v4-mini";
import type { Logger } from "@/utils/logger";
import {
  GqlAuthChannelType,
  sdkAuthUserSchema,
  sdkValidatedUserIdentifierSchema,
} from "@/state/sdk-auth-state";

const embedMessageEventName = "fanfest:embed-message";

export enum EmbedMessageType {
  EmbedReady = "embed-ready",
  Authentication = "authentication",
  AuthenticationFinished = "authentication-finished",
}

const embedReadyMessageSchema = z.object({
  type: z.literal(EmbedMessageType.EmbedReady),
});

const authenticationEmbedMessageSchema = z.object({
  type: z.literal(EmbedMessageType.Authentication),
  jwt: z.nullable(z.string()),
  validated_identifiers: z.nullable(z.array(sdkValidatedUserIdentifierSchema)),
  matches_identity: z.boolean(),
  auth_channel: z.nullable(z.enum(GqlAuthChannelType)),
});

const authenticationFinishedEmbedMessageSchema = z.object({
  type: z.literal(EmbedMessageType.AuthenticationFinished),
  user: sdkAuthUserSchema,
});

const embedMessageSchema = z.object({
  type: z.literal(embedMessageEventName),
  data: z.union([
    embedReadyMessageSchema,
    authenticationEmbedMessageSchema,
    authenticationFinishedEmbedMessageSchema,
  ]),
});

type EmbedMessage = z.infer<typeof embedMessageSchema>;
type OnMessageData = EmbedMessage["data"];

export class EmbedBroker extends SimpleObservable {
  #state: SDKState;
  #logger: Logger;
  onMessage = new SimpleObservableSubject<OnMessageData>();

  constructor(state: SDKState, logger: Logger) {
    super();
    this.#state = state;
    this.#logger = logger.extend(this.constructor.name);
  }

  init() {
    window.addEventListener("message", this.handleMessage);
  }

  detach() {
    window.removeEventListener("message", this.handleMessage);
    this.onMessage.clear();
  }

  sendMessage(message: EmbedMessage["data"], embedElement: HTMLIFrameElement) {
    const wind = embedElement.contentWindow ?? window;

    this.#logger.debug("Sending message", {
      message,
      embedElement,
      window: wind,
    });

    wind.postMessage(
      {
        type: embedMessageEventName,
        data: message,
      },
      this.#state.options.appOrigin,
    );
  }

  handleMessage = (event: MessageEvent) => {
    if (event.origin !== this.#state.options.appOrigin) return;
    if (event.data.type !== embedMessageEventName) return;

    const result = embedMessageSchema.safeParse(event.data);

    if (result.success) {
      this.notify(this.onMessage, result.data.data);
    } else {
      this.#logger.error(
        "Received message with invalid data",
        result.error,
        event,
      );
    }
  };
}
